import {
  EmitterEvent,
  EmitterEventTypes,
  EmitterGlobalSubscription,
  EmitterPayloadFromEventType,
  EmitterSubscription,
  EmitterUnsubscribe,
} from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'

interface EmitterServiceControl {
  subscribe: <TEventType extends EmitterEventTypes>(
    subscription: EmitterSubscription<TEventType>,
  ) => EmitterUnsubscribe
  subscribeAll: (subscription: EmitterGlobalSubscription) => EmitterUnsubscribe
  emit: <TEventType extends EmitterEventTypes>(
    eventType: TEventType,
    eventPayload: EmitterPayloadFromEventType<TEventType>,
  ) => void
}

class EmitterService implements EmitterServiceControl {
  private _subscriptions: Map<
    EmitterEventTypes,
    EmitterEvent<EmitterEventTypes>[]
  > = new Map()
  private _globalSubscriptions: EmitterGlobalSubscription[] = []
  private _logger: Logger

  //add a constructor that accepts a logger
  constructor(logger: Logger) {
    this._logger = logger
  }

  public subscribe = <TEventType extends EmitterEventTypes>(
    subscription: EmitterSubscription<TEventType>,
  ): EmitterUnsubscribe => {
    const { eventType, callback } = subscription
    const eventSubscriptions = this._subscriptions.get(eventType) ?? []
    eventSubscriptions.push(callback as EmitterEvent<EmitterEventTypes>)
    this._subscriptions.set(eventType, eventSubscriptions)

    return () => {
      const s = this._subscriptions.get(eventType) ?? []
      const index = s.indexOf(callback as EmitterEvent<EmitterEventTypes>)
      if (index >= 0) {
        s.splice(index, 1)
      }
    }
  }

  public subscribeAll = (
    subscription: EmitterGlobalSubscription,
  ): EmitterUnsubscribe => {
    this._globalSubscriptions.push(subscription)
    return () => {
      const index = this._globalSubscriptions.indexOf(subscription)
      if (index >= 0) {
        this._globalSubscriptions.splice(index, 1)
      }
    }
  }

  public emit = <TEventType extends EmitterEventTypes>(
    eventType: TEventType,
    eventPayload: EmitterPayloadFromEventType<TEventType>,
  ): void => {
    const eventSubscriptions = this._subscriptions.get(eventType) ?? []

    //iterate through all subscriptions and call them safely
    eventSubscriptions.forEach((callback) => {
      try {
        callback(eventPayload)
      } catch (e: unknown) {
        this._logger.error(`Error in event callback: ${e}`)
      }
    })

    //iterate through all global subscriptions and call them safely
    this._globalSubscriptions.forEach((globalSub) => {
      try {
        globalSub.callback(eventType, eventPayload)
      } catch (e: unknown) {
        this._logger.error(`Error in global event callback: ${e}`)
      }
    })
  }
}

const emitterServiceInstance = new EmitterService(new Logger('EmitterService'))
export default emitterServiceInstance as EmitterServiceControl
